import { useEffect, useMemo, useState } from "react";
import type { LocalStore, StoredDrill, StoredGame } from "@onchainreps/storage";
import { computeStyleProfile } from "../lib/style";

interface ProgressPanelProps {
  store: LocalStore | null;
  games: StoredGame[];
  onOpenDrills: () => void;
}

interface OpeningStat {
  opening: string;
  eco: string | null;
  accuracy: number;
  games: number;
}

export function ProgressPanel({ store, games, onOpenDrills }: ProgressPanelProps) {
  const [drills, setDrills] = useState<StoredDrill[]>([]);

  useEffect(() => {
    let active = true;
    if (!store) return;
    void store.listDrills().then((rows) => {
      if (active) setDrills(rows);
    });
    return () => {
      active = false;
    };
  }, [store]);

  const trend = useMemo(() => {
    return games
      .map((game) => ({
        id: game.id,
        accuracy: game.playerColor === "black" ? game.accuracyBlack : game.accuracyWhite,
        date: game.playedAt ?? game.importedAt,
      }))
      .filter((entry): entry is { id: string; accuracy: number; date: string } => entry.accuracy !== null)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [games]);

  const openings = useMemo<OpeningStat[]>(() => {
    const byOpening = new Map<string, { sum: number; count: number; eco: string | null }>();
    for (const game of games) {
      const accuracy = game.playerColor === "black" ? game.accuracyBlack : game.accuracyWhite;
      if (accuracy === null || !game.opening) continue;
      const entry = byOpening.get(game.opening) ?? { sum: 0, count: 0, eco: game.eco };
      entry.sum += accuracy;
      entry.count += 1;
      byOpening.set(game.opening, entry);
    }
    return [...byOpening.entries()]
      .map(([opening, entry]) => ({
        opening,
        eco: entry.eco,
        accuracy: entry.sum / entry.count,
        games: entry.count,
      }))
      .sort((a, b) => a.accuracy - b.accuracy)
      .slice(0, 6);
  }, [games]);

  const style = useMemo(() => computeStyleProfile(games), [games]);

  const average = trend.length
    ? trend.reduce((sum, entry) => sum + entry.accuracy, 0) / trend.length
    : null;

  if (!trend.length && !drills.length) return null;

  return (
    <section className="panel">
      <div className="panel__head">
        <h1 className="panel__title">Progress</h1>
        {average !== null ? (
          <span className="chip">
            <span className="chip__dot" />
            {average.toFixed(1)}% average
          </span>
        ) : null}
      </div>
      <div className="panel__body">
        <div className="progress-grid">
          <div className="progress__block">
            <h2 className="progress__title">Accuracy over time</h2>
            {trend.length ? (
              <div className="trend" role="img" aria-label="Accuracy over time">
                {trend.slice(-24).map((entry) => (
                  <span
                    className="trend__bar"
                    key={entry.id}
                    title={`${entry.accuracy.toFixed(1)}% · ${entry.date.slice(0, 10)}`}
                    style={{ height: `${Math.max(6, Math.min(100, entry.accuracy))}%` }}
                  />
                ))}
              </div>
            ) : (
              <p className="progress__note">Analyse a game to start the trend.</p>
            )}
          </div>

          <div className="progress__block">
            <h2 className="progress__title">Openings that cost you most</h2>
            {openings.length ? (
              <ul className="opstats">
                {openings.map((entry) => (
                  <li className="opstats__row" key={entry.opening}>
                    <span className="opstats__name">
                      {entry.opening}
                      {entry.eco ? <span className="opstats__eco">{entry.eco}</span> : null}
                    </span>
                    <span className="opstats__bar">
                      <span
                        className="opstats__fill"
                        style={{
                          width: `${Math.max(3, Math.min(100, entry.accuracy))}%`,
                          background:
                            entry.accuracy < 60
                              ? "var(--crimson)"
                              : entry.accuracy < 80
                                ? "var(--amber)"
                                : "var(--moss)",
                        }}
                      />
                    </span>
                    <span className="opstats__value">{Math.round(entry.accuracy)}%</span>
                    <span className="opstats__games">
                      {entry.games} game{entry.games === 1 ? "" : "s"}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="progress__note">No opening data yet.</p>
            )}
          </div>

          {style ? (
            <div className="progress__block">
              <h2 className="progress__title">Your style</h2>
              <div className="style">
                <div className="style__score">
                  <span className="style__score-value">{style.score}</span>
                  <span className="style__score-label">style score</span>
                </div>
                <div className="style__traits">
                  {style.traits.map((trait) => (
                    <div className="style__trait" key={trait.name}>
                      <span className="style__trait-name">{trait.name}</span>
                      <span className="style__trait-bar">
                        <span
                          className="style__trait-fill"
                          style={{ width: `${Math.max(4, trait.value)}%` }}
                        />
                      </span>
                      <span className="style__trait-value">{trait.value}</span>
                      <span className="style__trait-note">{trait.note}</span>
                    </div>
                  ))}
                </div>
              </div>
              <p className="progress__note">{style.headline}</p>
            </div>
          ) : null}

          <div className="progress__block">
            <h2 className="progress__title">Mistakes waiting</h2>
            {drills.length ? (
              <>
                <p className="progress__big">{drills.length}</p>
                <p className="progress__note">
                  positions from your own games are queued for drilling. The scheduler brings back the
                  ones you keep missing first.
                </p>
                <button className="btn btn--primary" onClick={onOpenDrills}>
                  Drill them now
                </button>
              </>
            ) : (
              <p className="progress__note">
                Nothing queued. Analyse a game and every blunder, miss and inaccuracy becomes a drill.
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
